import { Link } from "react-router-dom";
import { defineListView } from "@/base/list/defineListView";
import {
  MODIFIED_OR_CREATED_SORT,
  sortedDateColumn,
  sortedDateField,
} from "@/base/list/modifiedOrCreatedSort";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { getProducts } from "@/lib/brokerProducts";
import {
  listSubmissions,
  SUBMISSION_STATUSES,
  type AccountSubmission,
} from "@/lib/submissions/brokerSubmissions";
import { statusLabel } from "@/lib/listFormat";
import { SubmissionStatusChip } from "@/components/submissions/SubmissionStatusChip";
import { buttonClass } from "@/components/buttonStyles";

// The brokerage's own submissions as a BaseListView definition (BRK-S254,
// after BRK-S034). Each row opens the submission's detail and quoting view
// (BRK-S040).
//
// Every control is one PMS applies (`listSubmissions`): search over the
// submission number and the insured, the Status filter over PMS's nine
// statuses, the Product filter over the active products (read from
// GET /api/broker/products), sort by Last modified (PMS-S589) or Date created,
// and paging. Each row shows the submission's own status.

// The list's one call to action, on the one button style (BRK-S264).
const ACTION = buttonClass();

const STATUS_FILTER_OPTIONS = SUBMISSION_STATUSES.map((value) => ({
  value,
  label: statusLabel(value),
}));

export function submissionsListView({ request }: { request: AuthedRequester }) {
  return defineListView<AccountSubmission>({
    endpoint: "/api/broker/submissions",
    noun: "submissions",
    title: "Submissions",
    context: "Track your submissions to market.",
    primaryAction: (
      <Link to="/submissions/new" className={ACTION}>
        <span aria-hidden="true" className="text-base leading-none">
          +
        </span>
        New submission
      </Link>
    ),
    columns: [
      {
        id: "submission",
        header: "Submission",
        kind: "title",
        cell: (s) => <span className="tabular-nums">{s.submissionNumber}</span>,
        subtitle: (s) => s.accountLegalName ?? undefined,
      },
      { id: "coverage", header: "Coverage", cell: (s) => s.coverage },
      {
        id: "status",
        header: "Status",
        cell: (s) => <SubmissionStatusChip status={s.status} />,
      },
      sortedDateColumn<AccountSubmission>(),
      { id: "carrier", header: "Carrier", cell: (s) => s.carrier ?? "—" },
    ],
    rowKey: (s) => s.id,
    rowHref: (s) => `/submissions/${s.id}`,
    rowLabel: (s) =>
      s.accountLegalName
        ? `${s.submissionNumber}, ${s.accountLegalName}`
        : s.submissionNumber,
    search: {
      urlKey: "q",
      apiParam: "search",
      label: "Search submissions",
      placeholder: "Submission number or insured",
    },
    filters: [
      {
        urlKey: "status",
        apiParam: "status",
        label: "Status",
        allLabel: "All statuses",
        options: STATUS_FILTER_OPTIONS,
      },
      {
        urlKey: "product",
        apiParam: "productId",
        label: "Product",
        allLabel: "All products",
        options: {
          endpoint: "/api/broker/products",
          load: async () =>
            (await getProducts(request)).items.map((p) => ({
              value: p.id,
              label: p.name,
            })),
        },
      },
    ],
    sort: MODIFIED_OR_CREATED_SORT,
    pageSize: 25,
    empty: {
      title: "No submissions yet.",
      action: (
        <Link
          to="/submissions/new"
          className="text-sm font-medium text-brk-accent hover:underline"
        >
          Start your first submission →
        </Link>
      ),
    },
    load: ({ search, filters, sort, limit, offset }) =>
      listSubmissions(request, {
        search,
        // Narrowed from what BaseListView already checked against the
        // options, so the types say so too.
        status: SUBMISSION_STATUSES.find((s) => s === filters.status),
        productId: filters.productId,
        field: sortedDateField(sort),
        dir: sort?.dir,
        limit,
        offset,
      }),
  });
}
